import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, Table, Button, Space, Typography, Card, Row, Col, message, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ReceivableType } from '@/types';
import { ApartmentUnit } from '@/types/resources/property';
import { getPayablesByMonth } from '@/api/finance';
import { useAuth } from '@/contexts/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;

export interface ResidentReceivable {
  id: string;
  residentId: string;
  residentName: string;
  unitNumber: string;
  blockName: string;
  amount: number;
  editable: boolean;
}

export interface BulkReceivableData {
  name: string;
  type: ReceivableType;
  description: string;
  totalAmount: number;
  dueDate: dayjs.Dayjs;
  divisionMethod: 'equal' | 'percentage' | 'area' | 'bedrooms' | 'custom';
  customPercentages?: { [residentId: string]: number };
  residentReceivables: ResidentReceivable[];
}

interface BulkReceivableModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: (data: BulkReceivableData) => Promise<void>;
  loading?: boolean;
  occupiedUnits: ApartmentUnit[];
}

const BulkReceivableModal: React.FC<BulkReceivableModalProps> = ({
  open,
  onCancel,
  onSuccess,
  loading = false,
  occupiedUnits
}) => {
  const { t } = useTranslation(['receivables', 'common']);
  const { firestoreUser } = useAuth();
  const [form] = Form.useForm();
  const [residentReceivables, setResidentReceivables] = useState<ResidentReceivable[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [divisionMethod, setDivisionMethod] = useState<'equal' | 'percentage' | 'area' | 'bedrooms' | 'custom'>('equal');
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs());
  const [monthlyPayables, setMonthlyPayables] = useState<any[]>([]);
  const [loadingPayables, setLoadingPayables] = useState<boolean>(false);

  // Initialize resident receivables from occupied units
  useEffect(() => {
    if (open && occupiedUnits.length > 0) {
      const residents = occupiedUnits
        .filter(unit => unit.status === 'occupied' && unit.resident)
        .map(unit => ({
          id: `${unit.id}-${unit.resident!.id}`,
          residentId: unit.resident!.id,
          residentName: unit.resident!.name,
          unitNumber: unit.number,
          blockName: unit.block?.name || 'N/A',
          amount: 0,
          editable: true
        }));
      
      setResidentReceivables(residents);
    }
  }, [open, occupiedUnits]);

  // Fetch payables for the selected month
  const fetchMonthlyPayables = async (month: dayjs.Dayjs) => {
    if (!firestoreUser) return;
    
    setLoadingPayables(true);
    try {
      const payables = await getPayablesByMonth(month.year(), month.month() + 1, firestoreUser);
      setMonthlyPayables(payables);
      
      // Calculate total amount from payables
      const total = payables.reduce((sum, payable) => {
        return sum + (payable.amount?.amount || 0);
      }, 0);
      
      setTotalAmount(total);
      form.setFieldsValue({ totalAmount: total });
      
      console.log('📊 Monthly payables loaded:', {
        month: month.format('YYYY-MM'),
        count: payables.length,
        totalAmount: total
      });
    } catch (error) {
      console.error('Error fetching monthly payables:', error);
      message.error('Failed to load payables for the selected month');
    } finally {
      setLoadingPayables(false);
    }
  };

  // Load payables when modal opens or month changes
  useEffect(() => {
    if (open && firestoreUser) {
      fetchMonthlyPayables(selectedMonth);
    }
  }, [open, selectedMonth, firestoreUser]);

  // Calculate amounts based on division method
  const calculateAmounts = useMemo(() => {
    if (residentReceivables.length === 0 || totalAmount === 0) {
      return residentReceivables.map(r => ({ ...r, amount: 0 }));
    }

    switch (divisionMethod) {
      case 'equal':
        const equalAmount = totalAmount / residentReceivables.length;
        return residentReceivables.map(r => ({ ...r, amount: equalAmount }));
      
      case 'percentage':
        // For percentage, we'll need custom percentages input
        return residentReceivables.map(r => ({ ...r, amount: 0 }));
      
      case 'area':
        const totalArea = occupiedUnits.reduce((sum, unit) => sum + (unit.area || 0), 0);
        return residentReceivables.map(r => {
          const unit = occupiedUnits.find(u => u.resident?.id === r.residentId);
          const area = unit?.area || 0;
          const percentage = totalArea > 0 ? area / totalArea : 1 / residentReceivables.length;
          return { ...r, amount: totalAmount * percentage };
        });
      
      case 'bedrooms':
        const totalBedrooms = occupiedUnits.reduce((sum, unit) => sum + (unit.bedrooms || 0), 0);
        return residentReceivables.map(r => {
          const unit = occupiedUnits.find(u => u.resident?.id === r.residentId);
          const bedrooms = unit?.bedrooms || 0;
          const percentage = totalBedrooms > 0 ? bedrooms / totalBedrooms : 1 / residentReceivables.length;
          return { ...r, amount: totalAmount * percentage };
        });
      
      case 'custom':
        return residentReceivables; // Keep existing amounts
      
      default:
        return residentReceivables;
    }
  }, [residentReceivables, totalAmount, divisionMethod, occupiedUnits]);

  const handleTotalAmountChange = (value: number | null) => {
    setTotalAmount(value || 0);
  };

  const handleDivisionMethodChange = (value: 'equal' | 'percentage' | 'area' | 'bedrooms' | 'custom') => {
    setDivisionMethod(value);
  };

  const handleAmountChange = (residentId: string, amount: number) => {
    setResidentReceivables(prev => 
      prev.map(r => 
        r.residentId === residentId ? { ...r, amount } : r
      )
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const data: BulkReceivableData = {
        name: values.name,
        type: values.type,
        description: values.description,
        totalAmount: totalAmount,
        dueDate: values.dueDate,
        divisionMethod,
        residentReceivables: calculateAmounts
      };

      await onSuccess(data);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const columns = [
    {
      title: t('common:resident'),
      dataIndex: 'residentName',
      key: 'residentName',
      render: (text: string, record: ResidentReceivable) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.blockName} - Unit {record.unitNumber}
          </div>
        </div>
      ),
    },
    {
      title: t('common:amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 200,
      render: (amount: number, record: ResidentReceivable) => (
        <InputNumber
          value={amount}
          onChange={(value) => handleAmountChange(record.residentId, value || 0)}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
          precision={2}
          style={{ width: '100%' }}
          disabled={!record.editable}
        />
      ),
    },
  ];

  const receivableTypes = [
    { text: 'Rent', value: 'rent' },
    { text: 'Maintenance', value: 'maintenance' },
    { text: 'Utility', value: 'utility' },
    { text: 'Fine', value: 'fine' },
    { text: 'Other', value: 'other' },
  ];

  const divisionMethods = [
    { text: 'Equal Division', value: 'equal' },
    { text: 'By Area', value: 'area' },
    { text: 'By Bedrooms', value: 'bedrooms' },
    { text: 'Custom Amounts', value: 'custom' },
  ];

  const calculatedTotal = calculateAmounts.reduce((sum, r) => sum + r.amount, 0);
  const difference = totalAmount - calculatedTotal;

  return (
    <Modal
      title={t('receivables:bulkGeneration.title')}
      open={open}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t('common:actions.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={Math.abs(difference) > 0.01} // Allow small rounding differences
        >
          {t('receivables:bulkGeneration.createReceivables', { count: residentReceivables.length })}
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          type: 'other',
          dueDate: dayjs().add(30, 'days'),
        }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="month"
              label={t('receivables:bulkGeneration.monthLabel')}
              rules={[{ required: true, message: t('common:validation.required') }]}
              initialValue={selectedMonth}
            >
              <DatePicker
                picker="month"
                value={selectedMonth}
                onChange={(date) => {
                  if (date) {
                    setSelectedMonth(date);
                  }
                }}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="name"
              label={t('common:name')}
              rules={[{ required: true, message: t('common:validation.required') }]}
            >
              <Input placeholder={t('receivables:bulkGeneration.namePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="type"
              label={t('common:typeLabel')}
              rules={[{ required: true, message: t('common:validation.required') }]}
            >
              <Select>
                {receivableTypes.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label={t('common:description')}
          rules={[{ required: true, message: t('common:validation.required') }]}
        >
          <Input.TextArea rows={3} placeholder={t('receivables:bulkGeneration.descriptionPlaceholder')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="dueDate"
              label={t('common:dueDate')}
              rules={[{ required: true, message: t('common:validation.required') }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item 
              label={t('receivables:bulkGeneration.totalAmount')}
              extra={loadingPayables ? 'Loading payables...' : `${monthlyPayables.length} payables found`}
            >
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  value={totalAmount}
                  onChange={handleTotalAmountChange}
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
                  precision={2}
                  style={{ width: 'calc(100% - 40px)' }}
                  placeholder="0.00"
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => fetchMonthlyPayables(selectedMonth)}
                  loading={loadingPayables}
                  title="Refresh payables"
                />
              </Space.Compact>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('receivables:bulkGeneration.divisionMethod')}>
              <Select value={divisionMethod} onChange={handleDivisionMethodChange}>
                {divisionMethods.map(method => (
                  <Option key={method.value} value={method.value}>
                    {method.text}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Card size="small" style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={5} style={{ margin: 0 }}>
                {t('receivables:bulkGeneration.residentsSummary', { count: residentReceivables.length })}
              </Title>
            </Col>
            <Col>
              <Space>
                <Text strong>
                  {t('receivables:bulkGeneration.calculatedTotal')}: ${calculatedTotal.toFixed(2)}
                </Text>
                {Math.abs(difference) > 0.01 && (
                  <Text type={difference > 0 ? 'warning' : 'danger'}>
                    ({difference > 0 ? '+' : ''}${difference.toFixed(2)})
                  </Text>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        <Table
          columns={columns}
          dataSource={calculateAmounts}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ y: 300 }}
        />
      </Form>
    </Modal>
  );
};

export default BulkReceivableModal;
